/**
 * Trail Cursor Arrow — heading-rotated arrow markers for the GPS Trail Map.
 *
 * Used by:
 *   • Event Videos → Video Player Modal → Trail Map
 *     (`/app/frontend/src/components/media/EventTrailMap.jsx`)
 *   • Public Evidence Page Trail Map
 *     (`/app/frontend/src/pages/PublicEvidencePage.jsx`)
 *
 * Both pages share the same visual language: a speed-coloured arrow with a
 * white outline that rotates to match the vehicle's recorded heading. The
 * arrow is the "playhead" — its position is time-synced to the video's
 * current frame, so insurance / compliance recipients see exactly where
 * the vehicle was facing at any moment of the clip.
 *
 * The helpers here are intentionally provider-specific:
 *   • Leaflet wants an L.divIcon with inline rotated SVG (CSS rotate
 *     handles half a million dots per minute without re-rendering React).
 *   • Google Maps wants an icon descriptor that uses SymbolPath
 *     FORWARD_CLOSED_ARROW with a `rotation` property — Google's native
 *     symbol rasteriser is faster than a custom SVG marker.
 */
import L from 'leaflet';

/** Vehicle / playhead arrow as a Leaflet div icon. ``heading`` is degrees
 *  clockwise from North (0 = North, 90 = East). ``color`` is the speed
 *  colour (driven by the same gradient used for the trail dots).
 *
 *  Visual: a chunky white-outlined arrow with a small dark base ring so the
 *  arrow remains visible even on similarly-coloured tiles (e.g., a green
 *  arrow on a green park polygon). The Leaflet marker MUST have a real
 *  22x22 box and a centre anchor; the previous zero-size/overflow-visible
 *  icon could be painted outside the map pane on some browser/GPU paths.
 */
export const buildLeafletArrowIcon = ({ heading = 0, color = '#22c55e' }) => {
  const safeHeading = Number.isFinite(Number(heading)) ? Number(heading) : 0;
  // SVG is 22x22 and the marker anchor is [11,11], so the GPS coordinate
  // is exactly under the arrow's pivot. Rotation happens inside a centred
  // <g> rather than rotating the whole DOM node; this avoids browser-specific
  // transform-origin drift and clipping inside Leaflet panes.
  const svg = `
    <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg"
         style="display:block;filter:drop-shadow(0 1px 3px rgba(0,0,0,.55))">
      <g transform="translate(11 11) rotate(${safeHeading})">
        <circle cx="0" cy="0" r="9" fill="rgba(15,23,42,.55)" />
        <path d="M0 -8 L 6 7 L 0 4 L -6 7 Z"
              fill="${color}" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round" />
      </g>
    </svg>`.trim();
  return L.divIcon({
    className: 'trail-cursor-arrow-icon',
    html: `<div style="width:22px;height:22px;pointer-events:auto" data-testid="trail-cursor-arrow">${svg}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
};

/** Google Maps icon descriptor for the playhead arrow. Pass straight to
 *  ``new google.maps.Marker({ icon: buildGoogleArrowIcon(...) })``.
 *  Requires ``window.google`` to be loaded — caller MUST guard. */
export const buildGoogleArrowIcon = ({ heading = 0, color = '#22c55e' }) => {
  if (typeof window === 'undefined' || !window.google) return null;
  const safeHeading = Number.isFinite(Number(heading)) ? Number(heading) : 0;
  return {
    // Custom path that draws a 3-point arrow + a small base "tail" so it
    // reads as a vehicle direction icon (not a generic compass arrow).
    // The path is in Maps SVG path syntax; (0,0) is the GPS anchor.
    path: 'M 0 -8 L 6 7 L 0 4 L -6 7 Z',
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 1.5,
    scale: 1.4, // pixel scale — stays constant across zoom levels
    rotation: safeHeading,
    anchor: new window.google.maps.Point(0, 0),
  };
};
